import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { toastrError } from '@portals/redux/actions/toastr';
import { AuthType } from '@portals/types';

import { ORGANIZATIONS_API_URL } from './organizations.constants';
import { ServerError } from '../../types';
import { fetchApiRequest, useRequestOptions } from '../../utils';

export interface UseCreateOrganizationParams {
  org?: string;
  name?: string;
  email?: string;
  password?: string;
  afterAuthPath?: string;
  referral?: string | null;
  force_domain?: boolean;
  token: string;
  partner_name?: string;
  is_sandbox?: boolean;
}

export function useCreateOrganization() {
  const dispatch = useDispatch();
  const { url, options } = useRequestOptions({
    url: ORGANIZATIONS_API_URL,
    method: 'POST',
  });

  return useMutation<AuthType, ServerError, UseCreateOrganizationParams>({
    mutationFn: ({ afterAuthPath, ...params }) => {
      return fetchApiRequest(url, {
        ...options,
        body: JSON.stringify({
          ...params,
          accepted_terms_and_conditions: true,
        }),
      });
    },
    onError: ({ error }) => {
      dispatch(toastrError(error));
    },
    meta: {
      mutationName: 'useCreateOrganization',
      baseUrl: ORGANIZATIONS_API_URL,
      method: 'POST',
    },
  });
}

interface UseCheckOrganizationExistenceProps {
  email: string;
}

interface CheckOrganizationExistenceResponse {
  in_use: boolean;
}

export function useCheckOrganizationExistence() {
  const dispatch = useDispatch();
  const { url, options } = useRequestOptions({
    url: `${ORGANIZATIONS_API_URL}/check_domain`,
    method: 'POST',
  });

  return useMutation<
    CheckOrganizationExistenceResponse,
    ServerError,
    UseCheckOrganizationExistenceProps
  >({
    mutationFn: (params) => {
      return fetchApiRequest(url, {
        ...options,
        body: JSON.stringify(params),
      });
    },
    onError: ({ error }) => {
      dispatch(toastrError(error));
    },
    meta: {
      mutationName: 'useCheckOrganizationExistence',
      baseUrl: `${ORGANIZATIONS_API_URL}/check_domain`,
      method: 'POST',
    },
  });
}
